import { Text } from "@src/components/general/Text";
import {
  HorizontalFlexbox,
  VerticalFlexbox,
} from "@src/components/layout/Flexbox";
import { Button } from "@src/components/ui/button";
import { Progress } from "@src/components/ui/progress";
import type { ReactNode } from "react";
import { Wizard as UseWizard, useWizard } from "react-use-wizard";

export interface WizardProps {
  children: ReactNode;
  onFinish?: () => void;
  startIndex?: number;
}

const WizardHeader = () => {
  const { activeStep, stepCount } = useWizard();
  const percent = ((activeStep + 1) / stepCount) * 100;

  return (
    <VerticalFlexbox gap="xs" className="pb-4">
      <HorizontalFlexbox alignItems="center" justifyContent="space-between">
        <Text as="span" sizing="xs" fontWeight={500} appearance="subdued">
          Step {activeStep + 1} of {stepCount}
        </Text>
        <Text as="span" sizing="xs" fontWeight={500} appearance="subdued">
          {Math.round(percent)}%
        </Text>
      </HorizontalFlexbox>
      <Progress value={percent} />
    </VerticalFlexbox>
  );
};

interface WizardFooterProps {
  onFinish?: () => void;
}

const WizardFooter = ({ onFinish }: WizardFooterProps) => {
  const { isFirstStep, isLastStep, previousStep, nextStep, isLoading } =
    useWizard();

  const handleNext = () => {
    if (isLastStep && onFinish) {
      onFinish();
    } else {
      nextStep();
    }
  };

  return (
    <HorizontalFlexbox
      alignItems="center"
      justifyContent="space-between"
      gap="sm"
      className="mt-6"
    >
      {!isFirstStep ? (
        <Button
          type="button"
          variant="outline"
          onClick={previousStep}
          disabled={isLoading}
        >
          Previous
        </Button>
      ) : (
        <span aria-hidden />
      )}
      <Button
        type="button"
        variant="default"
        className="bg-accent text-accent-foreground hover:bg-accent/90"
        onClick={handleNext}
        disabled={isLoading}
      >
        {isLastStep ? "Finish" : "Next"}
      </Button>
    </HorizontalFlexbox>
  );
};

export const Wizard = ({ children, onFinish, startIndex = 0 }: WizardProps) => {
  return (
    <VerticalFlexbox className="w-full">
      <UseWizard
        startIndex={startIndex}
        header={<WizardHeader />}
        footer={<WizardFooter onFinish={onFinish} />}
      >
        {children}
      </UseWizard>
    </VerticalFlexbox>
  );
};
